import type { CSSProperties } from 'react';

/**
 * Width of the docked desktop planner panel. No minimum is needed: below the desktop
 * breakpoint the panel covers the screen. Used by the panel and by the page shell that
 * narrows to make room.
 */
export const AI_PLANNER_PANEL_WIDTH = '27%';

/**
 * CSS variable the page shell sets to the panel width while the planner is docked. Fixed
 * elements cannot follow the narrowed shell, so they subtract this from their width.
 */
export const AI_PLANNER_DOCK_WIDTH_VAR = '--ai-planner-dock-width';
export const AI_PLANNER_DOCK_WIDTH = `var(${AI_PLANNER_DOCK_WIDTH_VAR}, 0px)`;

/**
 * Stacking of the planner over the page. The docked panel sits under the app bar, the
 * expanded panel covers it, and both stay below full-screen modals. The entry button floats
 * under popups. On mobile the button lifts above the full-screen planner while it animates.
 */
export const AI_PLANNER_Z_INDEX = {
  panelDocked: 199,
  panelExpanded: 300,
  entry: 998,
  fullscreen: 10000,
  entryRaised: 10001,
  // Inside the entry button, over its rings
  bubble: 2,
} as const;

// Colours of the ring around the planner entry points
export const AI_PLANNER_RING_GRADIENT =
  'linear-gradient(135deg, #ffcf77 8%, #9462ff 53%, #44ddff 100%)';

/**
 * Background for the gradient ring. Pair it with a transparent border: the fill
 * covers the padding box and the gradient shows through the border.
 */
export const aiPlannerRingBackground = (fill: string): CSSProperties => ({
  background: `linear-gradient(${fill}, ${fill}) padding-box, ${AI_PLANNER_RING_GRADIENT} border-box`,
});

// The same colours swept round a circle, for the spinning ring on the mobile button
export const AI_PLANNER_RING_CONIC_GRADIENT =
  'conic-gradient(from 0deg, #ffcf77, #9462ff, #44ddff, #9462ff, #ffcf77)';

// Lets the full-screen planner find the mobile button it grows out of
export const AI_PLANNER_FAB_ID = 'ai-planner-fab';

// Cubic-bezier curves shared by the mobile button and the full-screen planner
export const AI_PLANNER_EASE = {
  // A light overshoot for things springing into place
  overshoot: [0.26, 1.36, 0.42, 1],
  // Speeds up to the end, for things leaving
  accelerate: [0.4, 0, 0.62, 0.2],
  // The browser's default `ease`
  standard: [0.25, 0.1, 0.25, 1],
  // Calm, with no overshoot, for the docked desktop panel and the page beside it
  dock: [0.32, 0.72, 0.28, 1],
} as const;

// How the docked panel slides, and the page beside it narrows, in step. Shared by both.
export const AI_PLANNER_DOCK_MOTION = {
  openMs: 380,
  closeMs: 320,
  easing: `cubic-bezier(${AI_PLANNER_EASE.dock.join(', ')})`,
} as const;
